import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { getProfile, IProfileRow } from "./getProfile";

interface IGetProfilePropertyResponceModel {
  /**
   * 対象チャネルの配列
   */
  channels: string[];
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.get(url);
  }
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからプロファイル一覧の取得が成功した場合に呼び出されます
   * @callback success
   * @param {IProfileRow[]} profiles - プロファイル一覧
   * @param {number} status - httpステータス
   */
  success: (profiles: IProfileRow[], status: number) => void;
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
  authService?: AuthService;
  projectid: string;
  threshold: number;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const data: IGetProfilePropertyResponceModel = res.data;
      const promises = data.channels.map((channel) => {
        return new Promise<IProfileRow>((resolve, reject) => {
          getProfile({
            authService: params.authService,
            projectid: params.projectid,
            channel: channel,
            threshold: params.threshold,
            isRetry401: false,
            success: (profile) => {
              resolve(profile);
            },
            fail: (id, status) => {
              reject({ id: id, status: status });
            },
          });
        });
      });
      Promise.all(promises)
        .then((values) => {
          const profiles = values.sort((a, b) => {
            if (a.channel > b.channel) {
              return 1;
            } else {
              return -1;
            }
          });
          params.success(profiles, 200);
        })
        .catch((err) => {
          const { id, status } = err;
          params.fail(id, status);
        });
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface GetProfilesModel {
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * データ間引き閾値
   */
  threshold: number;
}

export interface IGetProfilesParams extends GetProfilesModel, ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * プロファイル一覧を取得します
 * @function getProfiles
 * @param {IGetProfilesParams} params - プロファイル一覧取得パラメータ
 */
export function getProfiles(params: IGetProfilesParams): void {
  const { authService, projectid, threshold } = params;
  const url = WebAPI.PROFILE_PROPERTY(projectid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          authService: authService,
          projectid: projectid,
          threshold: threshold,
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getProfilesRetry401(authService, url, params, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      projectid: projectid,
      threshold: threshold,
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * プロファイル一覧を取得します
 * @function getProfiles
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {GetProfilesModel} model - プロファイル一覧取得モデル
 * @param {ICallbackParams} param - コールバックパラメータ
 */
export function getProfilesRetry401(
  authService: AuthService,
  url: string,
  model: GetProfilesModel,
  param: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        authService: authService,
        projectid: model.projectid,
        threshold: model.threshold,
        success: param.success,
        fail: param.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      param.fail(id, status);
    },
  });
}
